import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";

import "../assets/scss/components/_record-a-message-section.scss";
import { faMicrophone } from "@fortawesome/pro-regular-svg-icons/faMicrophone";
import { faPlay } from "@fortawesome/pro-regular-svg-icons/faPlay";
import { faRefresh } from "@fortawesome/pro-regular-svg-icons/faRefresh";
import { faStop } from "@fortawesome/pro-regular-svg-icons/faStop";
import { faVideo } from "@fortawesome/pro-regular-svg-icons/faVideo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useRef, useEffect } from "react";

import { RecordAMessageApi } from "../api/RecordAMessage";
import QR from "../assets/images/qr.gif";
import Record from "../assets/images/record.gif";
import Send from "../assets/images/send.gif";
import { useProductStore } from "../stores/ProductStore";
import { Image } from "./Image";
import { Slider } from "./Slider";
import { useRecordWebcam } from "./webcam";

const CAPTURE_OPTIONS_VIDEO = {
	audio: true,
	video: { facingMode: "user" },
};

const CAPTURE_OPTIONS_AUDIO = {
	audio: true,
	video: false,
};

const RECORD_MAX_LENGTH = 30000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const blobToBase64 = (blob) =>
	new Promise((resolve) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result);
		reader.readAsDataURL(blob);
	});

export const RecordAMessageSection = () => {
	const [showRecordAMessage, setShowRecordAMessage] = useState(false);
	const [recordingType, setRecordingType] = useState(false);
	const [trackStage, setTrackStage] = useState(0);
	const [isRecording, setIsRecording] = useState(false);

	return (
		<section className="record-a-message-section col-12">
			<span className="callout">New!</span>
			<strong className="section-title">Would you like to add on a FREE Audio or Video message?:</strong>
			<p>
				Record a video or audio message to send with your gift. You can record a message up to 30 seconds long
				and we will include it with the message card that you have chosen above.
			</p>

			<div className="record-a-message-container">
				<div
					className="record-a-message-track"
					style={{
						left: `${trackStage * -100}%`,
					}}
				>
					<div className="how-it-works">
						<Slider
							slides={[
								{
									element: (
										<div className="how-it-works-step record">
											<Image src={Record} alt="Record" />
											<p>Record your message</p>
										</div>
									),
								},
								{
									element: (
										<div className="how-it-works-step send">
											<Image src={Send} alt="Send" />
											<p>We'll send it with your message card</p>
										</div>
									),
								},
								{
									element: (
										<div className="how-it-works-step qr">
											<Image src={QR} alt="QR" />
											<p>They scan the code and enjoy</p>
										</div>
									),
								},
							]}
							slides_per_row={{ base: 1, md: 3 }}
							slide_duration={3}
							indicators={true}
							autoplay={true}
						/>
						<button className="record-a-message-button" onClick={() => setTrackStage(1)}>
							Get Started
						</button>
					</div>
					<div className="recording-type">
						<strong>Select your recording type:</strong>
						<div className="recording-type-options record-a-message-controls">
							<button
								className="back-button"
								onClick={() => {
									setTrackStage(0);
								}}
							>
								<FontAwesomeIcon icon={faArrowLeft} />
								Back
							</button>
							<button
								className={`recording-type-option ${recordingType === "video" ? "active" : ""}`}
								onClick={() => {
									setRecordingType("video");
									setTrackStage(2);
								}}
							>
								<FontAwesomeIcon icon={faVideo} />
								Video
							</button>
							<button
								className={`recording-type-option ${recordingType === "audio" ? "active" : ""}`}
								onClick={() => {
									setRecordingType("audio");
									setTrackStage(2);
								}}
							>
								<FontAwesomeIcon icon={faMicrophone} />
								Audio
							</button>
						</div>
					</div>
					<div className={`actual-recording ${isRecording ? "recording" : ""} ${recordingType} `}>
						{recordingType && (
							<RecordAMessage
								recordingType={recordingType}
								setRecordingType={setRecordingType}
								setTrackStage={setTrackStage}
								setIsRecording={setIsRecording}
								isRecording={isRecording}
							/>
						)}
					</div>
					<div className="message-complete">
						<p>Your message has been recorded and is ready to be sent with your gift.</p>
						<p>If you would like to start again please click below.</p>
						<button
							onClick={() => {
								setTrackStage(0);
								setRecordingType(false);
								setIsRecording(false);
							}}
						>
							Start Over
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

const RecordAMessage = ({ recordingType, setRecordingType, setTrackStage, isRecording, setIsRecording }) => {
	const { UploadVideoMsg } = useProductStore(({ UploadVideoMsg }) => ({ UploadVideoMsg }));
	const {
		createRecording,
		openCamera,
		startRecording,
		stopRecording,
		clearError,
		clearAllRecordings,
		activeRecordings,
	} = useRecordWebcam({
		mediaTrackConstraints: recordingType === "video" ? CAPTURE_OPTIONS_VIDEO : CAPTURE_OPTIONS_AUDIO,
		mediaRecorderOptions: {
			audioBitsPerSecond: 128000,
			videoBitsPerSecond: 512000,
		},
	});

	const [isCountingDown, setIsCountingDown] = useState(false);
	const [countDown, setCountDown] = useState(3);
	const [recordedFile, setRecordedFile] = useState(null);
	const [recordingId, setRecordingId] = useState(null);
	const [previewReady, setPreviewReady] = useState(false);

	const countDownRef = useRef(null);
	const timeoutRef = useRef(null);

	const resetState = async () => {
		setIsRecording(false);
		setIsCountingDown(false);
		setCountDown(3);
		setRecordedFile(null);
		setRecordingId(null);
		setPreviewReady(false);
		await clearAllRecordings();
	};

	const startCountdown = () => {
		setIsCountingDown(true);
		let count = 3;
		setCountDown(count);

		countDownRef.current = setInterval(() => {
			count -= 1;
			setCountDown(count);

			if (count === 0) {
				clearInterval(countDownRef.current);
				handleStartRecording();
				setIsCountingDown(false);
			}
		}, 1000);
	};

	const handleStartPreview = async () => {
		await clearAllRecordings();
		clearError();

		const newRecording = await createRecording();
		setRecordingId(newRecording.id);

		await openCamera(newRecording.id);
	};

	const handleStartRecording = async () => {
		if (!recordingId) return;

		await startRecording(recordingId);
		setIsRecording(true);

		timeoutRef.current = setTimeout(() => {
			handleStopRecording(recordingId);
		}, RECORD_MAX_LENGTH);
	};

	const handleStopRecording = async (id) => {
		clearTimeout(timeoutRef.current);
		setIsRecording(false);

		const file = await stopRecording(id);
		if (file?.blob) {
			const base64 = await blobToBase64(file.blob);
			setRecordedFile(base64);
		}
	};

	const handleSaveRecording = async () => {
		if (!recordedFile) return;
		const res = await UploadVideoMsg(recordingType, recordedFile);

		if (res?.location) {
			setTrackStage(3);
			console.log("Recording saved successfully:", res);
		} else {
			alert("There was an issue saving your recording. Please try again.");
		}
	};

	useEffect(() => {
		return () => {
			clearInterval(countDownRef.current);
			clearTimeout(timeoutRef.current);
			clearAllRecordings();
		};
	}, []);

	return (
		<div className="record-a-message-options row">
			<div className="record-a-message-option col-12 col-md-12">
				<div className="record-a-message-container">
					<div className="record-a-message-preview">
						{isCountingDown && <div className="countdown">{countDown}</div>}

						{activeRecordings.map((rec) => (
							<div key={rec.id}>
								{recordingType === "video" ? (
									<>
										{["INITIAL"].includes(rec.status) && (
											<div style={{ color: "white" }}>Loading camera...</div>
										)}
										<video
											ref={rec.webcamRef}
											autoPlay
											muted
											hidden={["STOPPED", "INITIAL"].includes(rec.status)}
											onCanPlay={() => setPreviewReady(true)}
										/>
										<video
											ref={rec.previewRef}
											controls
											hidden={["RECORDING", "INITIAL", "OPEN"].includes(rec.status)}
										/>
									</>
								) : (
									<>
										{["INITIAL", "OPEN"].includes(rec.status) && <div>Loading mic...</div>}
										<audio
											ref={rec.webcamRef}
											autoPlay
											controls
											hidden={["STOPPED", "INITIAL"].includes(rec.status)}
											onCanPlay={() => setPreviewReady(true)}
										/>
										<audio
											ref={rec.previewRef}
											controls
											hidden={["RECORDING", "INITIAL", "OPEN"].includes(rec.status)}
										/>
									</>
								)}
							</div>
						))}
					</div>

					<div className="record-a-message-controls">
						<button className="back-button" onClick={() => setTrackStage(1)}>
							<FontAwesomeIcon icon={faArrowLeft} />
							Back
						</button>

						{!recordedFile && (
							<button
								className="record-button"
								onClick={() => {
									if (isRecording) {
										handleStopRecording(recordingId);
									} else if (!recordingId) {
										handleStartPreview(); // Step 1: Open camera
									} else if (previewReady && !isCountingDown) {
										startCountdown(); // Step 2: Start countdown
									}
								}}
								disabled={isCountingDown}
							>
								<FontAwesomeIcon icon={isRecording ? faStop : faPlay} />
								{isRecording
									? "Stop"
									: !recordingId
										? "Get Started"
										: previewReady
											? "Record"
											: "Loading..."}
							</button>
						)}

						{recordedFile && (
							<>
								<button className="delete-button" onClick={resetState}>
									<FontAwesomeIcon icon={faRefresh} />
									Restart
								</button>
								<button className="save-button" onClick={handleSaveRecording}>
									<FontAwesomeIcon icon={faCheck} />
									Save
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
